/**
* ต่างจังหวัด — service-area coverage for every Thai province.
*
* COMPLIANCE: none of these points is a staffed office. They are generated
* as service-area entries (no street, no postcode) so the site never
* advertises an address it cannot verify. Google treats fabricated local
* addresses as spam; keep `serviceAreaOnly: true` here forever.
*/
import { PROVINCES } from "@/data/coverage";
import { branch, type Branch, type ServiceFocus } from "./types";

/** Provinces already represented by dedicated points elsewhere. */
const ALREADY_COVERED = new Set([
  "bangkok",
  "nonthaburi",
  "pathum-thani",
  "samut-prakan",
  "nakhon-pathom",
  "samut-sakhon",
  // จังหวัดที่มีสำนักงานจริง (ดู offices.ts)
  "khon-kaen",
  "udon-thani",
]);

/** Provinces with meaningful expat / border / tourism demand get wider focus. */
const FOCUS_OVERRIDES: Record<string, ServiceFocus[]> = {
  "chiang-mai": ["notary", "translation", "sworn", "naati", "lgz", "visa", "longstay", "marriage", "pcc"],
  "chiang-rai": ["notary", "translation", "lgz", "visa", "immigration", "pcc"],
  phuket: ["notary", "translation", "sworn", "lgz", "visa", "longstay", "marriage", "corporate", "estate"],
  "chon-buri": ["notary", "translation", "sworn", "lgz", "visa", "longstay", "marriage", "corporate"],
  "surat-thani": ["notary", "translation", "lgz", "visa", "longstay", "marriage"],
  krabi: ["notary", "translation", "lgz", "visa", "longstay", "marriage"],
  songkhla: ["notary", "translation", "lgz", "visa", "pcc", "corporate"],
  udonthani: ["notary", "translation", "lgz", "visa", "marriage", "pcc"],
  "udon-thani": ["notary", "translation", "lgz", "visa", "marriage", "pcc"],
  "khon-kaen": ["notary", "translation", "lgz", "visa", "marriage", "corporate"],
  "nong-khai": ["notary", "translation", "lgz", "visa", "immigration", "pcc"],
  tak: ["notary", "translation", "lgz", "visa", "immigration"],
  trang: ["notary", "translation", "sworn", "lgz", "visa", "marriage", "estate"],
  rayong: ["notary", "translation", "lgz", "visa", "corporate", "longstay"],
  "nakhon-ratchasima": ["notary", "translation", "lgz", "visa", "marriage", "corporate"],
  "prachuap-khiri-khan": ["notary", "translation", "lgz", "visa", "longstay", "marriage"],
  "buri-ram": ["notary", "translation", "lgz", "visa", "marriage"],
  "ubon-ratchathani": ["notary", "translation", "lgz", "visa", "marriage", "pcc"],
};

const DEFAULT_FOCUS: ServiceFocus[] = ["notary", "translation", "sworn", "lgz", "moj", "pcc"];

export const PROVINCE_BRANCHES: Branch[] = PROVINCES.filter(
  (p) => !ALREADY_COVERED.has(p.slug),
).map((p) =>
  branch({
    id: `th-${p.slug}`,
    name: `พื้นที่ให้บริการ${p.nameTh}`,
    shortName: p.nameTh,
    street: "",
    locality: `อำเภอเมือง${p.nameTh}`,
    region: p.nameTh,
    postalCode: "",
    branchType: "province",
    provinceSlug: p.slug,
    serviceAreaOnly: true,
    meetingPoint: `ตัวเมือง${p.nameTh} และอำเภอโดยรอบ`,
    landmarks: [`ศาลากลางจังหวัด${p.nameTh}`, `ที่ว่าการอำเภอเมือง${p.nameTh}`],
    transit: [],
    serviceFocus: FOCUS_OVERRIDES[p.slug] ?? DEFAULT_FOCUS,
    note: `รับงานรับรองเอกสารและแปลรับรองสำหรับลูกค้าใน${p.nameTh} แบบนัดหมายล่วงหน้า พร้อมรับ-ส่งเอกสารทางไปรษณีย์ลงทะเบียน/เคอรี่`,
  }),
);
